import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as React from 'react';
import classnames from '../utils/classnames.tsx';
import { faCheck, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { withIcon } from './Icon.tsx';
import { CheckboxBox } from './Checkbox.tsx';

const ChevronRightIcon = withIcon(faChevronRight);
const CheckIcon = withIcon(faCheck);

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuSubTrigger = ({
    className,
    inset,
    children,
    ...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.SubTrigger> & { inset?: boolean }) => (
    <DropdownMenuPrimitive.SubTrigger
        className={classnames(
            'flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm focus:bg-surface-strong data-[state=open]:bg-surface-strong',
            inset && 'pl-8',
            className
        )}
        {...props}
    >
        {children}
        <ChevronRightIcon className="ml-auto h-4 w-4" />
    </DropdownMenuPrimitive.SubTrigger>
);
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = ({
    className,
    ...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.SubContent>) => (
    <DropdownMenuPrimitive.SubContent
        className={classnames(
            'bg-surface-weak data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[12rem] overflow-hidden rounded-md border border-line-weak border-solid p-1 text-content-strong shadow-lg',
            className
        )}
        {...props}
    />
);
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = ({
    className,
    sideOffset = 4,
    ...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Content> & { sideOffset?: number }) => (
    <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
            sideOffset={sideOffset}
            collisionPadding={8}
            className={classnames(
                'dark-mode-ready',
                'flex flex-col overflow-auto gap-0.5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[12rem] rounded-md border border-line-weak border-solid bg-surface-weak p-1.5 text-content-strong shadow-md',
                className
            )}
            style={{
                maxHeight: 'calc(var(--radix-dropdown-menu-content-available-height) - 10px)',
                minWidth: 'min(calc(var(--radix-dropdown-menu-trigger-width) + 40px), calc(100vw - 1rem))', // 20px for scrollbar + 20px for padding
                maxWidth: 'calc(100vw - 1rem)',
            }}
            {...props}
        />
    </DropdownMenuPrimitive.Portal>
);
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = ({
    className,
    inset,
    danger = false,
    ...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Item> & { inset?: boolean; danger?: boolean }) => (
    <DropdownMenuPrimitive.Item
        className={classnames(
            'relative flex cursor-default select-none outline-none items-center rounded-sm px-3 py-2 text-sm text-content-strong transition-colors hover:text-content-strong hover:bg-surface-strong focus:bg-surface-strong data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
            inset && 'pl-8',
            danger &&
                'text-content-danger-strong hover:text-content-danger-strong hover:bg-surface-danger-weak focus:bg-surface-danger-weak',
            className
        )}
        {...props}
    />
);
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = ({
    className,
    children,
    checked,
    ...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.CheckboxItem>) => (
    <DropdownMenuPrimitive.CheckboxItem
        className={classnames(
            'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm text-content-strong transition-colors hover:bg-surface-strong focus:bg-surface-strong data-[disabled]:pointer-events-none data-[disabled]:opacity-50 group',
            className
        )}
        checked={checked}
        {...props}
    >
        <CheckboxBox
            size="sm"
            state={checked === 'indeterminate' ? 'indeterminate' : checked ? 'checked' : 'unchecked'}
            isDisabled={props.disabled}
            className="absolute right-2"
        />
        {children}
    </DropdownMenuPrimitive.CheckboxItem>
);
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = ({
    className,
    children,
    ...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.RadioItem>) => (
    <DropdownMenuPrimitive.RadioItem
        className={classnames(
            'relative flex cursor-default select-none items-center rounded-sm py-1.5 px-3 pr-8 text-sm text-content-strong outline-none transition-colors hover:bg-surface-strong focus:bg-surface-strong data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[state=checked]:bg-surface-brand-medium-default data-[state=checked]:text-content-brand-strong data-[state=checked]:hover:bg-surface-brand-medium-hover data-[state=checked]:active:bg-surface-brand-medium-active',
            className
        )}
        {...props}
    >
        <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
            <DropdownMenuPrimitive.ItemIndicator>
                <CheckIcon />
            </DropdownMenuPrimitive.ItemIndicator>
        </span>
        {children}
    </DropdownMenuPrimitive.RadioItem>
);
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuRadioGroup = ({
    className,
    ...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.RadioGroup>) => (
    <DropdownMenuPrimitive.RadioGroup className={classnames('flex flex-col gap-0.5', className)} {...props} />
);
DropdownMenuRadioGroup.displayName = DropdownMenuPrimitive.RadioGroup.displayName;

const DropdownMenuLabel = ({
    className,
    inset,
    ...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Label> & { inset?: boolean }) => (
    <DropdownMenuPrimitive.Label
        className={classnames('px-2 py-1.5 text-xs font-medium text-content-medium', inset && 'pl-8', className)}
        {...props}
    />
);
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = ({
    className,
    ...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Separator>) => (
    <DropdownMenuPrimitive.Separator
        className={classnames('-mx-1 my-1 border-t border-line-weak border-solid', className)}
        {...props}
    />
);
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({ className, ...props }: React.ComponentPropsWithRef<'span'>) => {
    return <span className={classnames('ml-auto text-xs tracking-widest opacity-60', className)} {...props} />;
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

export default Object.assign(DropdownMenu, {
    Trigger: DropdownMenuTrigger,
    Content: DropdownMenuContent,
    Item: DropdownMenuItem,
    CheckboxItem: DropdownMenuCheckboxItem,
    RadioItem: DropdownMenuRadioItem,
    Label: DropdownMenuLabel,
    Separator: DropdownMenuSeparator,
    Shortcut: DropdownMenuShortcut,
    Group: DropdownMenuGroup,
    Portal: DropdownMenuPortal,
    Sub: DropdownMenuSub,
    SubContent: DropdownMenuSubContent,
    SubTrigger: DropdownMenuSubTrigger,
    RadioGroup: DropdownMenuRadioGroup,
});
